import { createContext, useState, useEffect, useContext } from 'react';
import AcessoService, { CadastrarDto, IRealizarLoginDTO } from '@/services/AcessoService';
import Storage from '@/utils/StorageUtils';
import { RequestEditarPerfil } from '@/dtos/Request/RequestEditarPerfil';
import { EnderecoDto } from '@/dtos/EnderedoDto';
import { ConfirmarDadosCadastro } from '@/pages/PrimeiroAcesso';

interface IAuthContext {
    isAuthorized: Boolean,
    loading: Boolean,
    usuario: IRealizarLoginDTO | null,
    realizarLogin: (login: string, senha: string) => Promise<IRealizarLoginDTO>,
    cadastrarCliente: (cadastrarDto: CadastrarDto) => Promise<IRealizarLoginDTO>,
    cadastrarSenhaPrimeiroAcesso: (cadastrarSenhaPrimeiroAcesso: ConfirmarDadosCadastro) => Promise<IRealizarLoginDTO>,
    autenticarPorCodigo: (RecaptchaToken: string, CPF: string, Codigo: string, Email?: string) => Promise<IRealizarLoginDTO>,
    atualizarSaldoDisponivel: () => void
    realizarLoginPorToken: (token: string) => void
    editarPerfil: (dados: RequestEditarPerfil) => void
    saldoDisponivel: number,
    deslogar: () => void,
}

const AuthContext = createContext<IAuthContext | null>(null);

export const AuthProvider = ({ children }: any) => {
    const [isAuthorized, setAuthorized] = useState<boolean>(false);
    const [usuario, setUsuario] = useState<IRealizarLoginDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [saldoDisponivel, setSaldoDisponivel] = useState(0);
    const isAndroid = navigator.userAgent.toLowerCase().includes("android");
    const isInstagram = navigator.userAgent.toLowerCase().includes("instagram")

    useEffect(() => {
        async function loadStorageData() {
            enableSplashScreen();
            const storageUsuario = await Storage.getUsuario();

            if (storageUsuario) {
                setAuthorized(true);
                setUsuario(storageUsuario);
                await atualizarSaldoDisponivel();
            }

            setTimeout(() => {
                setLoading(false);
                disableSplashScreen();
            }, 400);
        }

        if(isAndroid && isInstagram && !window.location.href.includes('compra/extrato-compras') ){
            setTimeout(() => {
                setLoading(false);
                disableSplashScreen();
            }, 400);
            return;
        }

        loadStorageData();
    }, []);

    async function atualizarSaldoDisponivel() {
        const { Data: result } = await AcessoService.buscarSaldoCredito();
        setSaldoDisponivel(result);
    }

    const enableSplashScreen = () => {
        const splashScreen = document.getElementById('splash-screen')
        if (splashScreen) {
            splashScreen.style.setProperty('display', 'flex')
        }
    }

    const disableSplashScreen = () => {
        const splashScreen = document.getElementById('splash-screen')
        if (splashScreen) {
            splashScreen.style.setProperty('display', 'none')
        }
    }

    async function realizarLogin(login: string, senha: string): Promise<IRealizarLoginDTO> {
        const { Data: result } = await AcessoService.signIn(login, senha);

        await Storage.setUsuario(result)

        setAuthorized(true);
        setUsuario(result);
        atualizarSaldoDisponivel();
        return result;
    }

    async function realizarLoginPorToken(token: string) {
        const { Data: result } =  await AcessoService.autenticarPorToken(token);

        await Storage.setUsuario(result)

        setAuthorized(true);
        setUsuario(result);
        atualizarSaldoDisponivel();
        return result;
    }

    async function autenticarPorCodigo(RecaptchaToken: string,CPF: string, Codigo: string, Email?: string): Promise<IRealizarLoginDTO> {
        const { Data: result } = await AcessoService.autenticarPorCodigo(RecaptchaToken, CPF, Codigo, Email);

        await Storage.setUsuario(result)

        setAuthorized(true);
        setUsuario(result);
        atualizarSaldoDisponivel();
        return result;
    }

    async function cadastrarCliente(cadastrarDto: CadastrarDto): Promise<IRealizarLoginDTO> {
        const { Data: result } = await AcessoService.cadastrar(cadastrarDto);

        await Storage.setUsuario(result)

        setAuthorized(true);
        setUsuario(result);
        return result;
    }

    async function cadastrarSenhaPrimeiroAcesso(dados: ConfirmarDadosCadastro): Promise<IRealizarLoginDTO> {
        const { Data: result } = await AcessoService.cadastrarPrimeiroAcesso(dados);

        await Storage.setUsuario(result)

        setAuthorized(true);
        setUsuario(result);
        return result;
    }

    async function editarPerfil(dados: RequestEditarPerfil) {
        await AcessoService.editarCadastro(dados)

        if (!usuario) {
            return;
        }

        let newUsuario: IRealizarLoginDTO = {
            ...usuario,
            Nome: dados.Nome,
            NomeSocial: dados.NomeSocial,
            CPF: dados.CPF,
            DataNascimento: dados.DataNascimento,
            Telefone: dados.Telefone,
            Endereco: {
                CEP: dados.CEP,
                UF: dados.UF || '',
                Cidade: dados.Cidade,
                Bairro: dados.Bairro,
                Logradouro: dados.Logradouro,
                Numero: dados.Numero,
                Complemento: dados.Complemento,
            } as EnderecoDto
        } as IRealizarLoginDTO

        await Storage.setUsuario(newUsuario);

        setAuthorized(true);
        setUsuario(newUsuario);
    }

    async function deslogar(): Promise<void> {
        await Storage.clearStorage();
        setAuthorized(false);
        setUsuario(null);
    }



    return (
        <AuthContext.Provider value={{ realizarLoginPorToken, autenticarPorCodigo, cadastrarSenhaPrimeiroAcesso, isAuthorized, atualizarSaldoDisponivel, editarPerfil, loading, realizarLogin, cadastrarCliente, deslogar, usuario, saldoDisponivel }
        }>
            {children}
        </AuthContext.Provider >
    )
}

export function useAuth() {
    const context = useContext<IAuthContext | any>(AuthContext);

    return context as IAuthContext;
}